/**
 * Session 19e — OCPP 2.0.1 diagnostics outbound (CP → CSMS):
 *   - LogStatusNotification — progress of a GetLog-triggered upload.
 *   - NotifyEvent           — generic device-event stream (the hook the
 *                             Device Model needs once variable monitoring
 *                             lands; for now fired manually for QA).
 * v2.0.1-only; OcppV201Adapter methods, not on the version-neutral interface.
 */

export type UploadLogStatus =
  | 'BadMessage'
  | 'Idle'
  | 'NotSupportedOperation'
  | 'PermissionDenied'
  | 'Uploaded'
  | 'UploadFailure'
  | 'Uploading'
  | 'AcceptedCanceled';

export interface LogStatusNotificationDomain {
  readonly status: UploadLogStatus;
  readonly requestId?: number;
}

export function buildLogStatusNotificationRequest(
  req: LogStatusNotificationDomain,
): Record<string, unknown> {
  return {
    status: req.status,
    ...(req.requestId !== undefined ? { requestId: req.requestId } : {}),
  };
}

export type EventTrigger = 'Alerting' | 'Delta' | 'Periodic';
export type EventNotificationType =
  | 'HardWiredNotification'
  | 'HardWiredMonitor'
  | 'PreconfiguredMonitor'
  | 'CustomMonitor';

export interface EventData {
  readonly eventId: number;
  readonly timestamp: string;
  readonly trigger: EventTrigger;
  readonly actualValue: string;
  readonly eventNotificationType: EventNotificationType;
  readonly component: string;
  readonly variable: string;
  readonly cause?: number;
  readonly techInfo?: string;
  readonly cleared?: boolean;
}

export interface NotifyEventDomain {
  readonly generatedAt: string;
  readonly seqNo: number;
  readonly tbc: boolean;
  readonly eventData: readonly EventData[];
}

export function buildNotifyEventRequest(req: NotifyEventDomain): Record<string, unknown> {
  return {
    generatedAt: req.generatedAt,
    seqNo: req.seqNo,
    tbc: req.tbc,
    eventData: req.eventData.map((e) => ({
      eventId: e.eventId,
      timestamp: e.timestamp,
      trigger: e.trigger,
      actualValue: e.actualValue,
      eventNotificationType: e.eventNotificationType,
      component: { name: e.component },
      variable: { name: e.variable },
      ...(e.cause !== undefined ? { cause: e.cause } : {}),
      ...(e.techInfo !== undefined ? { techInfo: e.techInfo } : {}),
      ...(e.cleared !== undefined ? { cleared: e.cleared } : {}),
    })),
  };
}
